<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user';
import { formatNumberWithWan } from '@/utils'
import { usePvpCardGameStore } from '../services/gameService';
import { computed, onMounted, ref } from 'vue';
import { usePvpCardProfileStore } from '../services/gameProfile';
import { getFreeCardPackApi, getRecentLoseStreakApi, getWinsCardPackApi, openCardPackApi } from '@/api/pvp-card';
import DialogDeckResult from './DialogDeckResult.vue';
import { countBy, debounce } from 'lodash';
import { PvpCardCommodity } from '../services/enum';
import { dayjs, ElNotification } from 'element-plus';
import DialogGameRecord from './DialogGameRecord.vue';
import DialogRank from './DialogRank.vue';
import SoulCurrency from './SoulCurrency.vue';
import { getImportantNoticeApi } from '@/api/important-notice';
import { isTest } from '../services/tools';
import DialogGameIntro from './DialogGameIntro.vue';

const userStore = useUserStore()
const pvpCardGameStore = usePvpCardGameStore();
const pvpCardProfileStore = usePvpCardProfileStore()

const importantNotice = ref('')
async function getImportantNotice() {
  const res = await getImportantNoticeApi(2)
  if (res) importantNotice.value = res
}

const hasReadNotice = ref(sessionStorage.getItem('hasReadPvpNotice') === 'true')
onMounted(async () => {
  getImportantNotice()
  if (!isTest && !hasReadNotice.value) {
    ElNotification({
      dangerouslyUseHTMLString: true,
      message: `<div class="affix">
      <p>1. 本轮为<b class="shrink">删档测试</b>。</p>
      <p>2. 测试结束后，将<b class="shrink">重置</b><span class="info">养成进度</span>，并<span class="grow">1比1</span>返还魂玉。
      </p>
      </div>`,
      duration: 4000,
    })
    sessionStorage.setItem('hasReadPvpNotice', 'true')
    hasReadNotice.value = true
  }
  await pvpCardProfileStore.init()
  getRecentLoseStreak()
})
const loading = ref(false)

const joinGameHandler = debounce(async () => {
  loading.value = true
  await pvpCardGameStore.startMatchmaking()
  loading.value = false
}, 1000, { leading: true })
const cancelGameHandler = debounce(async () => {
  loading.value = true
  await pvpCardGameStore.cancelGame()
  loading.value = false
}, 1000, { leading: true })

const isFreeCardPackReady = computed(() => {
  const freeCardPackTime = pvpCardProfileStore.profile.freeCardPackTime
  if (!freeCardPackTime) {
    return false
  }
  const freeCardPackTimeObj = dayjs(freeCardPackTime).valueOf()
  const now = dayjs().valueOf()
  return freeCardPackTimeObj < now
})
const dialogDeckResultRef = ref<InstanceType<typeof DialogDeckResult>>()

const freeCardPackLoading = ref(false)
async function getFreeCardPack() {
  if (dayjs(pvpCardProfileStore.profile.freeCardPackTime).valueOf() > dayjs().valueOf()) {
    ElMessage.warning('免费卡包领取时间未到')
    return
  }
  try {
    freeCardPackLoading.value = true
    const res = await getFreeCardPackApi()
    res.coin && (userStore.consumables.duelCoin += res.coin)
    res.diamond && (userStore.consumables.duelDiamond += res.diamond)
    dialogDeckResultRef.value?.handleOpenResult({
      coin: res.coin,
      diamond: res.diamond || 0,
      cards: res.cardIds
    })
    await pvpCardProfileStore.init()
  } catch (error) {
    throw error
  }
  finally {
    freeCardPackLoading.value = false
  }
}

const winsCardPackLoading = ref(false)
async function getWinsCardPack() {
  if (pvpCardProfileStore.profile.winsCardPackCount < 10) {
    ElMessage.warning('对决卡包次数不足')
    return
  }
  try {
    winsCardPackLoading.value = true
    const res = await getWinsCardPackApi()
    res.coin && (userStore.consumables.duelCoin += res.coin)
    res.diamond && (userStore.consumables.duelDiamond += res.diamond)
    dialogDeckResultRef.value?.handleOpenResult({
      coin: res.coin,
      diamond: res.diamond,
      cards: res.cardIds
    })
    await pvpCardProfileStore.init()
  } catch (error) {
    throw error
  } finally {
    winsCardPackLoading.value = false
  }
}
const pendingCardPack = computed(() => {
  const list: any[] = []
  let t = 0
  while (t < 4) {
    const item = pvpCardProfileStore.profile.pendingCardPack[t]
    if (item) {
      list.push(item)
    } else {
      list.push(null)
    }
    t++
  }
  return list
})

async function handleUnLockCardPack(index: number) {
  const item = pendingCardPack.value[index]
  if (!item) {
    ElMessage.warning('卡包不存在')
    return
  }
  let usedDiamond = 0
  if (dayjs().valueOf() < dayjs(item.availableTime).valueOf()) {
    usedDiamond = Math.ceil((dayjs(item.availableTime).valueOf() - dayjs().valueOf()) / 1000 / 600)
    if (userStore.consumables.duelDiamond < usedDiamond) {
      ElMessage.warning(`卡包未到解锁时间，开启需要${usedDiamond}魂晶`)
      return
    }
    const result = await ElMessageBox.confirm(`确认花费${usedDiamond}魂晶解锁卡包吗？`, '卡包未到解锁时间', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).catch(() => false)
    if (!result) return
  }
  const loadingInstance = ElLoading.service({
    lock: true,
    text: '打开中...',
    fullscreen: true
  })
  try {
    const res = await openCardPackApi({ index })
    userStore.consumables.duelCoin += res.coin
    dialogDeckResultRef.value?.handleOpenResult({
      coin: res.coin,
      cards: res.cardIds
    })
    await pvpCardProfileStore.init()
    if (usedDiamond) {
      userStore.consumables.duelDiamond -= usedDiamond
    }
  } catch (error) {
    ElMessage.error('解锁卡包失败')
  } finally {
    loadingInstance.close()
  }
}

const hasTodayWinCardPackReceived = computed(() => {
  return dayjs(pvpCardProfileStore.profile.winCardPackTime).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')
})

const dialogGameRecordRef = ref<InstanceType<typeof DialogGameRecord>>()

function handleOpenGameRecord() {
  dialogGameRecordRef.value?.open()
}
const dialogRankRef = ref<InstanceType<typeof DialogRank>>()

function handleOpenSeasonRank() {
  dialogRankRef.value?.open()
}
const dialogGameIntroRef = ref<InstanceType<typeof DialogGameIntro>>()
function handleOpenGameIntro() {
  dialogGameIntroRef.value?.open()
}
const recentLoseStreak = ref(0)
function getRecentLoseStreak() {
  getRecentLoseStreakApi().then(res => {
    recentLoseStreak.value = res
  })
}
</script>
<template>
  <div class="duel-wrap">
    <slot name="top"></slot>
    <div class="top-bar">
      <h2 style="margin-bottom: 5px">
        战魂对决
      </h2>
      <el-alert v-if="importantNotice" :title="`通知：${importantNotice}`" type="warning" show-icon :closable="false"
        :style="{ margin: importantNotice ? '10px 0' : '5px 0' }" />
      <!-- 个人信息 -->
      <div>
        <div class="user-info">
          <span>
            ✨<span class="nickname" style="font-weight: bold" :style="{ background: userStore.nicknameColor }">{{
              userStore.nickname }}</span>
          </span>
          <div style="font-size: 12px">
            <span>
              <SoulCurrency type="dust" /> {{ formatNumberWithWan(userStore.consumables.duelCoin) }}
            </span>
            <span style="margin-left: 12px">
              <SoulCurrency type="quartz" /> {{ formatNumberWithWan(userStore.consumables.duelDiamond) }}
            </span>
            <span style="margin-left: 12px">
              <SoulCurrency type="jade" /> {{
                formatNumberWithWan(userStore.duelCrystal) }}
            </span>
          </div>
        </div>
        <div class="lv-and-exp">
          <span class="lv">lv{{ pvpCardProfileStore.playerLevel }}</span>
          <el-progress style="flex:1;" :text-inside="true" :stroke-width="20"
            :percentage="pvpCardProfileStore.playerPercent">
            <span style="color:#fff">{{ pvpCardProfileStore.playerExp }}/{{ pvpCardProfileStore.playerNextExp }}</span>
          </el-progress>
        </div>
      </div>
      <div class="card-group-wrap">
        <div class="free-card-group border-wrap gold" :class="{ isReady: isFreeCardPackReady }" @click="getFreeCardPack"
          v-loading="freeCardPackLoading">
          <div class="emoji-icon">
            🎁
          </div>
          <div>
            免费卡包
            <div class="open-text" v-show="isFreeCardPackReady">点击开启</div>
            <div class="wating-text" v-show="!isFreeCardPackReady && pvpCardProfileStore.profile.freeCardPackTime">
              <el-countdown format="HH:mm:ss" :value="dayjs(pvpCardProfileStore.profile.freeCardPackTime).valueOf()"
                :value-style="{ 'font-size': '14px' }" />
            </div>
          </div>
        </div>
        <!-- 卡包进度 -->
        <div class="win-card-gourp border-wrap diamond" @click="getWinsCardPack" v-loading="winsCardPackLoading">
          <div>
            <div class="title ">对决卡包</div>
            <div v-show="!hasTodayWinCardPackReceived">
              <div class="desc">打开需：{{ pvpCardProfileStore.profile.winsCardPackCount }}/10</div>
              <div class="progress">
                <div class="progress-item" v-for="item of pvpCardProfileStore.profile.winsCardPackCount" :key="item">
                </div>
              </div>
            </div>
            <div v-show="hasTodayWinCardPackReceived">今日已领取</div>
          </div>
          <div class="emoji-icon">
            🗃
          </div>
        </div>
      </div>
    </div>
    <!-- 战魂等级 -->
    <div class="soul-level-wrap">
      <div class="center-box">
        <div class="side">
          <div></div>
          <el-button size="small" type="success" plain @click="handleOpenGameIntro">玩法</el-button>
        </div>
        <div class="soul-level">
          <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false">
            <template #reference>
              <div class="name" :class="pvpCardProfileStore.profile.mmrParser.className">
                {{ pvpCardProfileStore.profile.mmrParser.name }}</div>
            </template>
            <div class="affix">
              <p>
                <span class="require">200💠</span>
                <span class="normal">淬炼战魂</span>
              </p>
              <p>
                <span class="require">600💠</span>
                <span class="race">稀有战魂</span>
              </p>
              <p>
                <span class="require">1200💠</span>
                <span class="epic">史诗战魂</span>
              </p>
              <p>
                <span class="require">2000💠</span>
                <span class="legend">传说战魂</span>
              </p>
              <p>
                <span class="require">3000💠</span>
                <span class="antiquity">远古战魂</span>
              </p>
              <p>
                <span class="require">4000💠</span>
                <span class="peerless">绝世战魂</span>
              </p>
            </div>
          </el-popover>
          <div class="score">{{ pvpCardProfileStore.profile.mmr }}💠</div>
        </div>
        <div class="side">
          <div></div>
          <el-button size="small" type="primary" plain @click="handleOpenGameRecord">战绩</el-button>
          <el-button size="small" type="warning" plain @click="handleOpenSeasonRank">排名</el-button>
        </div>
      </div>
      <!-- 对决按钮 -->
      <el-button v-if="!pvpCardGameStore.isWaiting" v-loading="loading" @click="joinGameHandler()" type="warning"
        size="large" style="width: 100px;font-size: 24px;box-sizing:content-box;">对决!</el-button>
      <el-button v-else v-loading="loading" @click="cancelGameHandler()" type="danger" size="large"
        style="width: 100px;box-sizing:content-box;">
        <div>
          <div style="font-size: 18px;">
            匹配中
          </div>
          <div style="font-size: 12px;margin-top: 4px;">{{ pvpCardGameStore.countdownTime }}秒</div>
        </div>
      </el-button>
      <div v-show="recentLoseStreak > 2" class="streak">
        当前连败{{ recentLoseStreak }}场
        <template v-if="recentLoseStreak >= 3">
          ，下次胜利必定获得专家以上卡包
        </template>
        <template v-if="recentLoseStreak >= 5">
          ，并补齐剩余卡包空位
        </template>
      </div>
    </div>
    <!-- 卡包待开槽 -->
    <div class="card-group">
      <div class="card-group-item" v-for="(item, index) of pendingCardPack" :key="index">
        <div v-if="item" class="card-pack" :class="item.key" @click="handleUnLockCardPack(index)">
          <div class="card-pack-name">{{ PvpCardCommodity[item.key] }}</div>
          <template v-if="dayjs().valueOf() < dayjs(item.availableTime).valueOf()">
            <el-countdown format="HH:mm:ss" :value="dayjs(item.availableTime).valueOf()"
              :value-style="{ 'font-size': '12px' }" class="card-pack-timer" />
            <div :style="{ 'font-size': '12px', 'text-align': 'center' }">
              <div>使用
                <SoulCurrency type="quartz" />
              </div>
              <div>立即开启</div>
            </div>
          </template>
          <div v-else :style="{ 'font-size': '12px', 'margin-top': '8px' }">可开启</div>
        </div>
      </div>
    </div>

    <DialogDeckResult ref="dialogDeckResultRef" />
    <DialogGameRecord ref="dialogGameRecordRef" />
    <DialogRank ref="dialogRankRef" />
    <DialogGameIntro ref="dialogGameIntroRef" />
  </div>
</template>
<style lang="scss" scoped>
.duel-wrap {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
}

.top-bar {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .user-info {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    padding: 8px 16px;
    margin-bottom: 10px;
  }

  .lv-and-exp {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    .lv {
      font-size: 14px;
      color: #fff;
      width: 40px;
      padding-left: 8px;
    }
  }
}

.card-group-wrap {
  display: flex;
  gap: 10px;
  white-space: nowrap;
}



.win-card-gourp,
.free-card-group {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;

  &.isReady {
    .emoji-icon {
      animation: jello-horizontal 0.9s both infinite;
    }
  }

  .open-text,
  .waiting-text {
    font-size: 12px;
    text-align: center;
    margin-top: 4px;
  }

  .title {
    font-size: 18px;
    font-weight: bold;
  }

  .emoji-icon {
    font-size: 42px;
  }

  @media screen and (max-width: 370px) {
    .emoji-icon {
      font-size: 32px;
    }
  }

  @media screen and (max-width: 320px) {
    .emoji-icon {
      display: none;
    }
  }

  .desc {
    font-size: 12px;
  }

  .progress {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    padding: 1px;
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    gap: 2px;
    overflow: hidden;
    height: 12px;

    .progress-item {
      width: 100%;
      height: 100%;
      background-image: linear-gradient(to bottom, #0055ff, #0099ff);
    }
  }
}

.soul-level-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  padding-bottom: 10px;

  .center-box {
    width: 100%;
    display: flex;
    justify-content: center;

    .side {
      flex: 1;
      display: grid;
      grid-template-rows: repeat(4, 1fr);
      align-items: center;
      gap: 8px;
    }

    button {
      margin: 0;
    }
  }
}

.soul-level {
  width: 220px;
  text-align: center;
  padding: 20px;

  .require {
    display: inline-block;
    width: 7ch;
    text-align: right;
  }

  .name {
    font-size: 24px;
    margin-bottom: 10px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    height: 80px;
    width: 180px;
    font-weight: bold;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
    // transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    // transform-style: preserve-3d;
    animation: shine 3s ease-in-out infinite;
    background-size: 200% 200%;

    &::before {
      content: '';
      position: absolute;
      inset: -6px;
      z-index: -1;
      background: inherit;
      clip-path: inherit;
      filter: blur(12px);
      opacity: 0.7;
      pointer-events: none;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 4px;
      z-index: -1;
      background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.4), transparent 70%);
      clip-path: inherit;
      pointer-events: none;
    }

    /* Noob 类别 */
    &.noob {
      background-image: linear-gradient(135deg, #999999 0%, #777777 100%);
      clip-path: polygon(100% 0%, 100% 100%, 0% 100%, 0% 0%);
      filter: drop-shadow(0 8px 16px rgba(121, 121, 121, 0.4));
    }

    /* Normal 类别 */
    &.normal {
      background-image: linear-gradient(135deg, #95b 0%, #7a4f9c 100%);
      clip-path: polygon(0% 0%, 100% 0%, 100% 70%, 50% 100%, 0% 70%);
      filter: drop-shadow(0 8px 16px rgba(121, 87, 156, 0.4));
    }

    /* Race 类别 */
    &.race {
      background-image: linear-gradient(135deg, #3e62ed 0%, #2a3cc5 100%);
      clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
      filter: drop-shadow(0 8px 16px rgba(62, 98, 237, 0.4));
    }

    /* Epic 类别 */
    &.epic {
      background-image: linear-gradient(135deg, #8d4bbb 0%, #6b3d94 100%);
      clip-path: polygon(0% 0%, 50% 20%, 100% 0%, 100% 70%, 50% 100%, 0% 70%);
      filter: drop-shadow(0 8px 16px rgba(141, 75, 187, 0.4));
    }



    /* Legend 类别 */
    &.legend {
      background-image: linear-gradient(135deg, #d66a35 0%, #b74a12 100%);
      clip-path: polygon(0% 0%, 50% 20%, 100% 0%, 100% 35%, 80% 50%, 80% 60%, 80% 70%, 80% 80%, 50% 100%, 20% 80%, 20% 70%, 20% 60%, 20% 50%, 0% 35%);
      filter: drop-shadow(0 8px 16px rgba(214, 106, 53, 0.4));
    }

    /* Antiquity 类别 */
    &.antiquity {
      background-image: linear-gradient(135deg, #f05654 0%, #d22b2a 100%);
      clip-path: polygon(0% 0%, 40% 20%, 50% 0%, 60% 20%, 100% 0%, 100% 35%, 80% 50%, 80% 60%, 80% 70%, 80% 80%, 50% 100%, 20% 80%, 20% 70%, 20% 60%, 20% 50%, 0% 35%);
      filter: drop-shadow(0 8px 16px rgba(240, 86, 84, 0.4));
    }

    /* Peerless 类别 */
    &.peerless {
      background-image: linear-gradient(135deg, #ff0097 0%, #d60073 100%);
      clip-path: polygon(0% 0%, 40% 20%, 50% 0%, 60% 20%, 100% 0%, 100% 35%, 80% 50%, 80% 60%, 95% 50%, 95% 60%, 80% 70%, 80% 80%, 50% 100%, 20% 80%, 20% 70%, 5% 60%, 5% 50%, 20% 60%, 20% 50%, 0% 35%);
      filter: drop-shadow(0 8px 16px rgba(255, 0, 151, 0.4));
    }
  }

  .score {
    font-size: 18px;
    font-weight: bold;
  }

}

.card-group {
  width: 100%;
  height: 80px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 20px;
  margin-bottom: 40px;

  .card-group-item {
    height: 100px;
    border: 1px solid rgba(140, 140, 140);
    box-shadow: 1px 2px 10px -4px rgba(255, 255, 255, 0.7) inset,
      1px 2px 4px rgba(70, 70, 70);
    border-radius: 4px;
    overflow: hidden;
    position: relative;

    &::before {
      content: "卡包空位";
      font-size: 12px;
      color: rgba(255, 255, 255, 0.3);
      text-align: center;
      display: block;
      margin-top: 20px;
    }
  }
}

.card-pack {
  width: 100%;
  height: 100%;
  background-image: linear-gradient(to bottom, #f6f6f6, var(--color));
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;

  .card-pack-name {
    font-size: 14PX;
  }

}

.streak {
  font-size: 14px;
  color: #b74a12;
  padding: 16px 20px 0;
}

@keyframes shine {

  0%,
  100% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }
}
</style>
<style lang="scss">
.normalDeck {
  --color: #59b;
}

.raceDeck {
  --color: #3e62ed;
}

.epicDeck {
  --color: #8d4bbb;
}

.legendDeck {
  --color: #d66a35;
}
</style>